import type {
  AnswerRequestStatus,
  BuilderAnswerOption,
  BuilderAnswerType,
  BuilderMode,
  BuilderQuestion,
  BuilderSection,
  ResponseReviewStatus,
} from '@/api/questionnaires/types';

export const QUESTIONNAIRE_STATES = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
} as const;

/**
 * Structured query params for the questionnaires list — `fetchQuestionnaires`
 * serialises these into the legacy URL shape (JSON `filter`, 0-indexed page,
 * top-level `isTemplate`/`isArchived`).
 */
export interface QuestionnairesListParams {
  /** 1-indexed page from the URL state. */
  page: number;
  /** Page size. */
  count: number;
  isArchived: boolean;
  /** "<field>:ASC" | "<field>:DESC" */
  sort?: string;
  /** Free-text name filter. */
  name?: string;
  /** One or more `QUESTIONNAIRE_STATES` keys. */
  status?: string[];
}

export interface MyTemplatesListParams {
  /** 1-indexed page from the URL — the API call subtracts 1. */
  page: number;
  count: number;
  /** "<field>:ASC" | "<field>:DESC" */
  sort?: string;
  /** Free-text name filter merged into the JSON filter param. */
  name?: string;
}

/** Structured params for `fetchMyQuestionnaireRequests`. */
export interface MyQuestionnaireRequestsListParams {
  /** 1-indexed page from the URL — the API call subtracts 1. */
  page: number;
  count: number;
  /** "<field>:ASC" | "<field>:DESC" */
  sort?: string;
  /** Free-text filter on questionnaire name (merged into JSON `filter`). */
  name?: string;
  /** Free-text filter on supplier company name. */
  about?: string;
  /** Company id filter for the "Requested By" column. */
  requestedByCompanyId?: string;
}

// ─── Responder (answer a questionnaire request) ───────────────────────────────

/** Status of a questionnaire request from the responder's POV. Only `OPEN`
 *  and `REQUEST_CHANGES` allow editing; `IN_REVIEW` and `COMPLETED` are
 *  read-only. */
export const ANSWER_REQUEST_STATUSES = {
  OPEN: 'OPEN',
  IN_REVIEW: 'IN_REVIEW',
  COMPLETED: 'COMPLETED',
  REQUEST_CHANGES: 'REQUEST_CHANGES',
} as const;

// ─── Builder (questionnaire/template editor) ──────────────────────────────────

/** Answer types the builder can insert. The first five use v2's Pascal-case
 *  identifiers (driven by the new builder schema); the remaining six keep
 *  the legacy ALL_CAPS keys so existing questionnaires loaded from the
 *  server round-trip identically and the same strings the old frontend
 *  sends are sent here too. */
export const BUILDER_ANSWER_TYPES = {
  SHORT_TEXT: 'ShortText',
  LONG_TEXT: 'LongText',
  YES_OR_NO: 'YesOrNo',
  NUMBER: 'Number',
  SELECT: 'Select',
  /** Legacy types — Pascal-case wrappers aren't introduced because the
   *  builder doesn't render bespoke editors yet; the legacy key flows
   *  through to the response page (old frontend), which already knows
   *  how to render them. */
  DATE: 'DATE',
  DOCUMENTS: 'DOCUMENTS',
  AUDIT: 'AUDIT',
  CERTIFICATE: 'CERTIFICATE',
  NUMBER_UOM: 'NUMBER_UOM',
  /** Multiple image upload. A plain image-upload answer (array of
   *  `{ id, url, fileType }`) — NOT an asset/document link. Kept distinct
   *  from DOCUMENTS so the responder uploads images instead of creating a
   *  company document. */
  PHOTO_UPLOAD: 'MULTIPLE_PHOTO_UPLOAD',
  /** Single video upload. Stores one `{ id, url, fileType }` on the answer
   *  (or `null`) — like PHOTO_UPLOAD but capped at one video. NOT the
   *  asset-link/document flow, which would wrongly create a company
   *  document. */
  VIDEO: 'VIDEO_UPLOAD',
  CHECK_BOXES: 'CHECK_BOXES',
  /** Multi-row tabular data — column schema lives on the datapoint's
   *  `meta.columns`. The preview renders a dynamic-row mini-table; the
   *  builder doesn't currently insert these (load-only). */
  TABLE: 'TABLE',
} as const;

export interface QuestionnaireResponsesListParams {
  /** 1-indexed page from the URL — the API call subtracts 1. */
  page: number;
  count: number;
  /** "<field>:ASC" | "<field>:DESC" */
  sort?: string;
  /** Free-text on the answering company's name. */
  name?: string;
  /** Free-text on the supplier name. */
  supplierName?: string;
  /** One or more `QuestionnaireResponseRow['status']` values. */
  status?: string[];
}

/** PUT payload mirrors the detail shape (minus statistics). */
export interface SaveBuilderPayload {
  name: string;
  description: string;
  sections: BuilderSection[];
  questions: BuilderQuestion[];
  dueDate?: string | null;
  passthrough?: Record<string, unknown>;
}

// ─── Question Library sidebar (Tier 1) ─────────────────────────────────────

/** The All / Templates / Custom toggle in the library sidebar. */
export type LibraryTab = 'all' | 'templates' | 'custom';

/** Where a library node came from. Both categories and questions carry it
 *  so the tab filter can prune entire branches without recursing into
 *  leaves. */
export type LibrarySource = 'template' | 'custom';

/** A leaf row in the library — a single insertable question. The
 *  `answerType` is one of the supported BUILDER_ANSWER_TYPES values;
 *  items whose server `fieldTypeId` doesn't map are dropped at the api
 *  layer. */
export interface LibraryQuestion {
  kind: 'question';
  /** Stable id from the server (or the answer-type key for Custom items). */
  id: string;
  /** Display label. For Custom items this is a translation key resolved
   *  by the renderer; for template items it's the raw backend label. */
  name: string;
  answerType: BuilderAnswerType;
  source: LibrarySource;
  /** Optional pre-defined options for SELECT / CHECK_BOXES / asset-link
   *  items. Carried through to the inserted BuilderQuestion. */
  options?: BuilderAnswerOption[];
  /** Optional icon class for the row — Custom items pass it through so
   *  legacy types (date/audit/etc.) get type-specific iconography. */
  icon?: string;
  /** Mirrors the data-point's `type === 'GLOBAL'`. Carried over to the
   *  inserted BuilderQuestion so the card can render the GLOBAL badge. */
  isGlobal?: boolean;
  /** Pre-filled unit key from `meta.unit` (NUMBER_UOM template
   *  questions). Carried through to the inserted BuilderQuestion. */
  unit?: string;
  /** The data-point's `retracedId`. Carried onto the BuilderQuestion as
   *  `retracedDatapointId` so the server keeps the question bound to its
   *  source data point — also drives the read-only state in the builder. */
  retracedDatapointId?: string;
}

/** A collapsible category node. Children can be either sub-categories or
 *  questions — the tree is genuinely recursive. */
export interface LibraryCategory {
  kind: 'category';
  id: string;
  /** Display label. Top-level groups get their name from the backend
   *  (impact-areas) except for `'custom'`, which uses a translation key. */
  name: string;
  source: LibrarySource;
  children: LibraryNode[];
}

/** Either a sub-category or a question — a node anywhere in the tree. */
export type LibraryNode = LibraryCategory | LibraryQuestion;

/** Asset bucket — one per answer type. Drives which `AssetTypeOption.type`
 *  values the picker shows. The string values match the server's
 *  `linkedAssets` keys (see `frontend/src/lib/constants.js:2458-2469`).
 *  `CERTIFICATE` is a meta-bucket: certificate questions accept items
 *  with `type` in `{ CERTIFICATE, SCOPE, PRODUCT, TRANSACTION, COMPANY,
 *  MEMBERSHIP }` — see `ASSET_BUCKET_ACCEPTS`. */
export type AssetBucket = 'COMPANY_DOCUMENT' | 'AUDIT' | 'CERTIFICATE';

/** Which `AssetTypeOption.type` values each bucket accepts. */
export const ASSET_BUCKET_ACCEPTS: Record<AssetBucket, readonly string[]> = {
  COMPANY_DOCUMENT: ['COMPANY_DOCUMENT'],
  AUDIT: ['AUDIT'],
  CERTIFICATE: [
    'CERTIFICATE',
    'SCOPE',
    'PRODUCT',
    'TRANSACTION',
    'COMPANY',
    'MEMBERSHIP',
  ],
};

/** Display label for an asset's `type` field — used by the badge on
 *  picker options and selected rows. Mirrors the old frontend's
 *  `linkedAssets.raw` map. */
export const LINKED_ASSET_LABELS: Record<string, string> = {
  CERTIFICATE: 'Certificate',
  SCOPE: 'Certificate:Scope',
  PRODUCT: 'Certificate:Product',
  TRANSACTION: 'Certificate:Transaction',
  COMPANY: 'Certificate:Company',
  MEMBERSHIP: 'Certificate:Membership',
  COMPANY_DOCUMENT: 'Document',
  AUDIT: 'Audit',
  FILE: 'Other',
  COMPANY_TABLE: 'Company',
};

export function linkedAssetLabel(
  type: string | null | undefined,
): string | null {
  if (!type) return null;
  return LINKED_ASSET_LABELS[type] ?? type;
}

// ─── Responder answer values ───────────────────────────────────────────────
// Controlled value shapes for the Responder* primitives. Each answer-type
// maps to one `kind`; the parent owns a `Map<questionId, AnswerValue>` and
// each field receives its slice through `value` + `onChange`. The preview
// and the real responder route share this contract.

export interface NumberWithYearAnswer {
  value: number | null;
  unit: string | null;
  year: number | null;
}

export type TableCellValue =
  | string
  | number
  | { value: number | null; unit: string | null; year: string | null }
  | null;

export interface ResponderTableRow {
  /** Stable client-side id used as the React key for the row. */
  key: string;
  values: Record<string, TableCellValue>;
}

/** A single uploaded image on a PHOTO_UPLOAD answer. Matches the shape the
 *  shared `InlineEditorImageListField` produces and the old frontend stored. */
export interface ResponderImage {
  id: string;
  url: string;
  fileType: string;
}

/** A single uploaded video on a VIDEO answer. Same wire shape as an image
 *  (`{ id, url, fileType }`) — matches `InlineEditorVideoField` and the
 *  company-profile video datapoint. */
export type ResponderVideo = ResponderImage;

export type AnswerValue =
  | { kind: 'text'; value: string }
  | { kind: 'number'; value: number | null }
  | { kind: 'numberUom'; value: NumberWithYearAnswer }
  | { kind: 'date'; value: string }
  | { kind: 'select'; value: string | null }
  | { kind: 'multi'; value: string[] }
  | { kind: 'table'; value: ResponderTableRow[] }
  /** Uploaded images for a PHOTO_UPLOAD question. */
  | { kind: 'images'; value: ResponderImage[] }
  /** A single uploaded video for a VIDEO question, or `null` when unset. */
  | { kind: 'video'; value: ResponderVideo | null }
  /** One linked-asset id per option slot. `null` = unset for that slot. */
  | { kind: 'assetLinks'; value: (string | null)[] };

/** Maps an answerType string to the `kind` it serializes into. */
export function kindForAnswerType(
  answerType: BuilderAnswerType | string,
): AnswerValue['kind'] {
  switch (answerType) {
    case BUILDER_ANSWER_TYPES.LONG_TEXT:
    case BUILDER_ANSWER_TYPES.SHORT_TEXT:
      return 'text';
    case BUILDER_ANSWER_TYPES.NUMBER:
      return 'number';
    case BUILDER_ANSWER_TYPES.NUMBER_UOM:
      return 'numberUom';
    case BUILDER_ANSWER_TYPES.DATE:
      return 'date';
    case BUILDER_ANSWER_TYPES.YES_OR_NO:
    case BUILDER_ANSWER_TYPES.SELECT:
      return 'select';
    case BUILDER_ANSWER_TYPES.CHECK_BOXES:
      return 'multi';
    case BUILDER_ANSWER_TYPES.TABLE:
      return 'table';
    case BUILDER_ANSWER_TYPES.PHOTO_UPLOAD:
      return 'images';
    case BUILDER_ANSWER_TYPES.VIDEO:
      return 'video';
    case BUILDER_ANSWER_TYPES.DOCUMENTS:
    case BUILDER_ANSWER_TYPES.AUDIT:
    case BUILDER_ANSWER_TYPES.CERTIFICATE:
      return 'assetLinks';
    default:
      return 'text';
  }
}

/** Empty starting value for the given answerType — used to seed
 *  uncontrolled→controlled migrations and to compare against for the
 *  "is this answered?" check. */
export function defaultAnswerValue(
  answerType: BuilderAnswerType | string,
  options?: BuilderAnswerOption[] | undefined,
): AnswerValue {
  switch (kindForAnswerType(answerType)) {
    case 'text':
      return { kind: 'text', value: '' };
    case 'number':
      return { kind: 'number', value: null };
    case 'numberUom':
      return {
        kind: 'numberUom',
        value: { value: null, unit: null, year: null },
      };
    case 'date':
      return { kind: 'date', value: '' };
    case 'select':
      return { kind: 'select', value: null };
    case 'multi':
      return { kind: 'multi', value: [] };
    case 'table':
      return { kind: 'table', value: [] };
    case 'images':
      return { kind: 'images', value: [] };
    case 'video':
      return { kind: 'video', value: null };
    case 'assetLinks': {
      const slots = Math.max(1, options?.length ?? 1);
      return { kind: 'assetLinks', value: Array(slots).fill(null) };
    }
  }
}

/** Whether a stored answer counts as "filled" for progress tracking. */
export function isAnswerFilled(v: AnswerValue | undefined): boolean {
  if (!v) return false;
  switch (v.kind) {
    case 'text':
      return v.value.trim().length > 0;
    case 'number':
      return v.value !== null;
    case 'numberUom':
      return v.value.value !== null;
    case 'date':
      return v.value.length > 0;
    case 'select':
      return v.value !== null;
    case 'multi':
      return v.value.length > 0;
    case 'table':
      return v.value.some(r =>
        Object.values(r.values).some(c => {
          if (c === null || c === undefined) return false;
          if (typeof c === 'string') return c.length > 0;
          if (typeof c === 'number') return true;
          return c.value != null || Boolean(c.unit) || Boolean(c.year);
        }),
      );
    case 'video':
      return v.value !== null;
    case 'assetLinks':
      return v.value.some(slot => slot !== null && slot.length > 0);
  }
}

// ─── Responder wire types ──────────────────────────────────────────────────

/** Each question on the responder endpoint carries its saved answer + the
 *  per-answer review state. Reuses `BuilderQuestion` for the question
 *  metadata so the UI primitives don't care which endpoint loaded the
 *  question. */
export interface AnswerRequestQuestion extends BuilderQuestion {
  /** Saved answer payload. The legacy backend stores this as a JSON string
   *  on the wire — the responder API layer leaves it as-is and the
   *  serializer parses it once. `null` means the responder hasn't saved
   *  any answer for this question. */
  answer: string | null;
  reviewStatus: ResponseReviewStatus;
  /** `1` when the responder marked the question as "not available";
   *  `0` otherwise. */
  isNotAvailable: 0 | 1;
}

/** Stats block exposed by the responder endpoint — drives the
 *  right-rail summary card (totals, percentages, last-update). */
export interface AnswerRequestStatistics {
  totalQuestions: number;
  totalResponses: number;
  totalApprovedResponses: number;
  totalInReviewResponses: number;
  totalDeniedResponses: number;
  totalMissingResponses: number;
  approvedResponsesPercentage: number;
  inReviewResponsesPercentage: number;
  deniedResponsesPercentage: number;
  missingResponsesPercentage: number;
  totalComments?: number;
  responsesPercentage?: number;
}

export interface AnswerRequest {
  id: string;
  questionnaireId: string;
  name: string;
  description: string;
  requestStatus: AnswerRequestStatus;
  /** Company the request was issued to (the responding-company id). */
  forCompanyId: string;
  /** Company the responder is acting on behalf of (multi-account). */
  onBehalfOfCompanyId: string;
  /** Convenience copy of `forCompanyId` on the legacy wire — sometimes
   *  diverges, callers should prefer `forCompanyId`. */
  companyId: string;
  /** Display name for the on-behalf-of company — used in the
   *  "Update value on X profile" toggle and the summary card's
   *  "About" row. */
  companyName: string;
  /** Display name + id for the requesting company (the buyer / brand).
   *  Shown on the summary card's "Requested by" row. */
  requestCompanyId: string;
  requestCompanyName: string;
  sections: BuilderSection[];
  questions: AnswerRequestQuestion[];
  createdAt: string | null;
  /** Last save timestamp (server-side). */
  updatedAt: string | null;
  /** Name of the user whose action last touched the request. */
  updatedByUserName: string | null;
  /** ISO date the responder must submit by. */
  dueDate?: string | null;
  statistics?: AnswerRequestStatistics;
}

/** Normalised shape used by the popover UI — flattens the legacy
 *  `updatedBy*` author fields and renames `questionComment → content`. */
export interface QuestionComment {
  id: string;
  content: string;
  /** ISO datetime. */
  commentedAt: string;
  commentedByUserId: string | null;
  commentedByUserName: string | null;
  commentedByCompanyId: string | null;
  commentedByCompanyName: string | null;
  repliedCommentId: string | null;
  edited: boolean;
}

/** Props for the shared preview / responder shell (`PreviewPage`). */
export interface PreviewPageProps {
  mode: BuilderMode;
  id: string;
  /** When true the page renders a hand-crafted mock questionnaire that
   *  covers every supported answer type. Skips the live detail query.
   *  Triggered via the `?mock=true` query param on the preview route. */
  isMock?: boolean;
  /** Fired from the banner's Exit button. The route owns navigation. */
  onExit: () => void;
}
